import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  GripVertical,
  MailX,
  MoreHorizontal,
  Plus,
  Printer,
  Settings2,
  Trash2,
} from 'lucide-react';
import {
  OFFICE_TIME_ZONE,
  isChecklistItemDone,
  taskDueBucket,
  type Checklist,
  type ChecklistItem,
  type ChecklistSection,
} from '@peles/shared';
import {
  useAddChecklistItem,
  useAddChecklistSection,
  useDeleteChecklist,
  useDeleteChecklistSection,
  useReorderChecklistItems,
  useReorderChecklistSections,
  useUpdateChecklist,
  useUpdateChecklistSection,
} from '../../hooks/useEventOps';
import { useDialog } from '../../context/DialogContext';
import { useArchiveTemplate } from '../../hooks/useTemplates';
import { useQueryClient } from '@tanstack/react-query';
import { DropdownMenu } from '../common/DropdownMenu';
import { InlineEditText } from '../common/InlineEditText';
import { SaveAsTemplateButton } from '../event-ops/SaveAsTemplateButton';
import { QuickAddTask } from './QuickAddTask';
import { TaskRow } from './TaskRow';
import { TaskConversationsProvider } from './TaskConversations';
import { TaskListPrintModal } from './TaskListPrintModal';
import { TaskListSettingsModal } from './TaskListSettingsModal';
import type { TaskFilters } from './TaskFilterBar';

export interface TaskListsViewProps {
  checklists: Checklist[];
  todayKey: string;
  /** Filters applied to what is shown (search and assignee); the order is kept. */
  /** The same filters the due view applies, applied here to what is shown; the order is kept. */
  filters: TaskFilters;
  currentUserId?: string;
  /** Scoped to one event: its start, for event-relative due dates. */
  eventStartAt?: Date | string | null;
  showEvent?: boolean;
  /** A list to bring into view on arrival (`?open=<id>` from the Templates page). */
  openId?: string | null;
  onOpen: (task: ChecklistItem, checklist: Checklist) => void;
  onToggle: (task: ChecklistItem) => void;
  onDelete: (task: ChecklistItem) => void;
}

function matches(
  task: ChecklistItem,
  filters: TaskListsViewProps['filters'],
  todayKey: string,
  currentUserId?: string,
): boolean {
  // A due filter reads a task's date the way the due view groups it.
  if (filters.due.length && !filters.due.includes(taskDueBucket(task.dueDate, todayKey))) {
    return false;
  }
  const q = filters.q.trim().toLowerCase();
  if (q && !`${task.title} ${task.notes ?? ''}`.toLowerCase().includes(q)) return false;
  if (filters.assignee.length) {
    const ok = filters.assignee.some((a) =>
      a === 'unassigned'
        ? !task.assignedToUserId
        : task.assignedToUserId === (a === 'me' ? currentUserId : a),
    );
    if (!ok) return false;
  }
  return true;
}

/** Lists shown before "Show more lists", and tasks shown per section before "Show more". */
export const LIST_PAGE_SIZE = 10;
export const SECTION_PAGE_SIZE = 50;

/** Client-side paging: the first `pageSize` items, and a way to reveal the next page. */
function usePaged<T>(items: T[], pageSize: number, atLeast = 0) {
  const [requested, setRequested] = useState(pageSize);
  const shown = Math.max(requested, atLeast);
  return {
    visible: items.slice(0, shown),
    hidden: Math.max(items.length - shown, 0),
    // One page more than is on screen now — exactly what the button says.
    showMore: () => setRequested((count) => Math.max(count, atLeast) + pageSize),
  };
}

function ShowMore({
  hidden,
  noun,
  onClick,
}: {
  hidden: number;
  noun: string;
  onClick: () => void;
}) {
  if (hidden <= 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mx-3 my-1 px-3 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
    >
      Show {Math.min(hidden, noun === 'lists' ? LIST_PAGE_SIZE : SECTION_PAGE_SIZE)} more {noun} (
      {hidden} hidden)
    </button>
  );
}

function progressOf(items: ChecklistItem[]) {
  const done = items.filter(isChecklistItemDone).length;
  return {
    done,
    total: items.length,
    pct: items.length ? Math.round((done / items.length) * 100) : 0,
  };
}

function ProgressBar({ pct, label }: { pct: number; label: string }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden"
    >
      <div
        className="h-full rounded-full bg-green-500 transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function SortableTask(
  props: Omit<Parameters<typeof TaskRow>[0], 'handle'> & { draggable: boolean },
) {
  const { draggable, ...rowProps } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task.id,
    disabled: !draggable,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TaskRow
        {...rowProps}
        handle={
          draggable ? (
            <button
              type="button"
              {...attributes}
              {...listeners}
              aria-label={`Move ${props.task.title}`}
              className="shrink-0 mt-0.5 -ml-1 p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
            >
              <GripVertical size={16} />
            </button>
          ) : undefined
        }
      />
    </div>
  );
}

interface SectionBlockProps {
  section: ChecklistSection;
  checklist: Checklist;
  index: number;
  sectionCount: number;
  props: TaskListsViewProps;
  onMove: (sectionId: string, direction: -1 | 1) => void;
}

function SectionBlock({
  section,
  checklist,
  index,
  sectionCount,
  props,
  onMove,
}: SectionBlockProps) {
  const { todayKey, filters, currentUserId, onOpen, onToggle, onDelete } = props;
  const { confirm } = useDialog();
  const addItem = useAddChecklistItem();
  const updateSection = useUpdateChecklistSection();
  const deleteSection = useDeleteChecklistSection();
  const reorder = useReorderChecklistItems();
  const [showDone, setShowDone] = useState(false);
  const [order, setOrder] = useState(() => section.items.map((i) => i.id));

  useEffect(() => {
    setOrder(section.items.map((i) => i.id));
  }, [section.items]);

  const byId = useMemo(() => new Map(section.items.map((i) => [i.id, i])), [section.items]);
  const ordered = order.map((id) => byId.get(id)).filter((i): i is ChecklistItem => !!i);
  const filtering =
    !!filters.q.trim() ||
    filters.assignee.length > 0 ||
    filters.due.length > 0 ||
    filters.status !== 'open';
  const visible = ordered.filter((t) => matches(t, filters, todayKey, currentUserId));
  // A section with no tasks has nothing for a filter to hide: it stays, with
  // its quick add, as the place a new list's first task goes. It keeps that
  // until the filter changes — adding the first task must not take away the
  // box the next one is being typed into, or hide a task the filter does not
  // match the moment it is added.
  const empty = section.items.length === 0;
  const filterKey = JSON.stringify(filters);
  const [heldFor, setHeldFor] = useState<string | null>(empty ? filterKey : null);
  useEffect(() => {
    if (empty) setHeldFor(filterKey);
  }, [empty, filterKey]);
  const held = empty || heldFor === filterKey;
  // Open (the default): open tasks, the done ones folded away. Done: only
  // the done ones. All: both, the done ones shown.
  const open = filters.status === 'done' ? [] : visible.filter((t) => !isChecklistItemDone(t));
  const done = visible.filter(isChecklistItemDone);
  const doneShown = showDone || filters.status !== 'open';
  const openPage = usePaged(open, SECTION_PAGE_SIZE);
  const donePage = usePaged(done, SECTION_PAGE_SIZE);
  const progress = progressOf(section.items);
  const eventStartAt = props.eventStartAt ?? checklist.event?.startAt ?? null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(order, from, to);
    setOrder(next);
    reorder.mutate(
      { sectionId: section.id, itemIds: next },
      { onError: () => setOrder(section.items.map((i) => i.id)) },
    );
  }

  async function removeSection() {
    const count = section.items.length;
    if (
      await confirm({
        title: 'Delete section',
        message: count
          ? `Delete “${section.name}” and its ${count} task${count === 1 ? '' : 's'}?`
          : `Delete “${section.name}”?`,
        confirmLabel: 'Delete',
        variant: 'danger',
      })
    ) {
      deleteSection.mutate(section.id);
    }
  }

  // With a filter on, a section none of whose tasks match would only be noise.
  if (filtering && !held && visible.length === 0) return null;

  return (
    <div
      className="border-t border-gray-100 first:border-t-0"
      data-testid={`section-${section.id}`}
    >
      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
        <InlineEditText
          value={section.name}
          label="Section name"
          maxLength={255}
          onSave={(name) => updateSection.mutateAsync({ sectionId: section.id, updates: { name } })}
          className="text-xs font-semibold uppercase tracking-wide text-gray-500"
        />
        <span className="text-xs text-gray-400" data-testid={`progress-${section.id}`}>
          {progress.done}/{progress.total}
        </span>
        <div className="ml-auto">
          <DropdownMenu
            ariaLabel={`Section ${section.name}`}
            align="end"
            trigger={(p) => (
              <button
                type="button"
                {...p}
                aria-label={`Actions for section ${section.name}`}
                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <MoreHorizontal size={15} />
              </button>
            )}
            items={[
              {
                id: 'up',
                label: 'Move up',
                icon: <ArrowUp size={14} />,
                disabled: index === 0,
                onSelect: () => onMove(section.id, -1),
              },
              {
                id: 'down',
                label: 'Move down',
                icon: <ArrowDown size={14} />,
                disabled: index === sectionCount - 1,
                onSelect: () => onMove(section.id, 1),
              },
              {
                id: 'delete',
                label: 'Delete section',
                icon: <Trash2 size={14} />,
                dividerBefore: true,
                onSelect: removeSection,
              },
            ]}
          />
        </div>
      </div>

      <div className="px-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={openPage.visible.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            {openPage.visible.map((task) => (
              <SortableTask
                key={task.id}
                task={task}
                todayKey={todayKey}
                isTemplate={checklist.isTemplate}
                showTime={checklist.dueTimeEnabled}
                draggable={!filtering}
                onToggle={onToggle}
                onOpen={(t) => onOpen(t, checklist)}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
        <ShowMore hidden={openPage.hidden} noun="tasks" onClick={openPage.showMore} />
        {(!filtering || held) && (
          <QuickAddTask
            todayKey={todayKey}
            eventStartAt={eventStartAt}
            withDate={!checklist.isTemplate}
            withTime={checklist.dueTimeEnabled}
            placeholder={`Add a task to ${section.name}`}
            onAdd={({ title, dueDate, dueTime }) =>
              addItem.mutate({
                sectionId: section.id,
                title,
                ...(dueDate ? { dueDate } : {}),
                ...(dueTime ? { dueTime } : {}),
              })
            }
          />
        )}
        {done.length > 0 && (
          <div className="px-3 pb-2">
            {filters.status === 'open' && (
              <button
                type="button"
                onClick={() => setShowDone((v) => !v)}
                aria-expanded={showDone}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 py-1"
              >
                {showDone ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                {done.length} done
              </button>
            )}
            {doneShown &&
              donePage.visible.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  todayKey={todayKey}
                  isTemplate={checklist.isTemplate}
                  showTime={checklist.dueTimeEnabled}
                  onToggle={onToggle}
                  onOpen={(t) => onOpen(t, checklist)}
                  onDelete={onDelete}
                />
              ))}
            {doneShown && (
              <ShowMore hidden={donePage.hidden} noun="done tasks" onClick={donePage.showMore} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistCard({ checklist, props }: { checklist: Checklist; props: TaskListsViewProps }) {
  const { confirm, prompt } = useDialog();
  const [expanded, setExpanded] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const updateChecklist = useUpdateChecklist();
  const deleteChecklist = useDeleteChecklist();
  const archiveTemplate = useArchiveTemplate();
  const queryClient = useQueryClient();
  const addSection = useAddChecklistSection();
  const reorderSections = useReorderChecklistSections();
  const items = checklist.sections.flatMap((s) => s.items);
  const progress = progressOf(items);
  // The Templates page names a template by its templateName; show and rename that.
  const title =
    (checklist.isTemplate ? checklist.templateName : null) || checklist.title || 'Untitled list';

  useEffect(() => {
    if (props.openId !== checklist.id) return;
    document
      .querySelector(`[data-testid="checklist-${checklist.id}"]`)
      ?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [props.openId, checklist.id]);

  async function newSection() {
    const name = await prompt({
      title: 'New section',
      message: 'What should the section be called?',
      confirmLabel: 'Add section',
    });
    if (name?.trim()) addSection.mutate({ checklistId: checklist.id, name: name.trim() });
  }

  async function removeList() {
    if (
      await confirm({
        title: 'Delete task list',
        message: `Delete “${title}” and its ${items.length} task${items.length === 1 ? '' : 's'}? This cannot be undone.`,
        confirmLabel: 'Delete',
        variant: 'danger',
      })
    ) {
      deleteChecklist.mutate(checklist.id);
    }
  }

  // A template is archived, never deleted — it is reusable work, as on the Templates page.
  async function archiveList() {
    if (
      await confirm({
        title: 'Archive template',
        message: `Archive “${title}”? It will no longer be offered for new events.`,
        confirmLabel: 'Archive',
        variant: 'warning',
      })
    ) {
      archiveTemplate.mutate(
        { id: checklist.id, type: 'checklist' },
        { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checklists'] }) },
      );
    }
  }

  function move(sectionId: string, direction: -1 | 1) {
    const ids = checklist.sections.map((s) => s.id);
    const from = ids.indexOf(sectionId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    reorderSections.mutate({ checklistId: checklist.id, sectionIds: arrayMove(ids, from, to) });
  }

  const eventDate = checklist.event
    ? new Date(checklist.event.startAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: OFFICE_TIME_ZONE,
      })
    : null;

  return (
    <section
      className="bg-white rounded-xl border border-gray-200"
      aria-label={title}
      data-testid={`checklist-${checklist.id}`}
    >
      <header className="px-4 pt-3 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
            className="p-0.5 rounded text-gray-500 hover:bg-gray-100"
          >
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <CheckSquare size={18} className="text-green-600 shrink-0" />
          <InlineEditText
            value={title}
            label="List name"
            // A template's name is also its templateName, which holds 255.
            maxLength={checklist.isTemplate ? 255 : 500}
            onSave={(next) =>
              updateChecklist.mutateAsync({
                id: checklist.id,
                updates: checklist.isTemplate
                  ? { title: next, templateName: next }
                  : { title: next },
              })
            }
            className="font-semibold text-gray-900"
          />
          {props.showEvent &&
            (checklist.event ? (
              <Link
                to={`/events/${checklist.event.id}/checklist`}
                className="hidden sm:inline-flex items-center gap-1 text-xs text-primary-700 bg-primary-50 hover:bg-primary-100 px-2 py-0.5 rounded-full"
              >
                <CalendarDays size={11} />
                {checklist.event.name} · {eventDate}
              </Link>
            ) : (
              <span className="hidden sm:inline text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                No event
              </span>
            ))}
          <div className="ml-auto flex items-center gap-1">
            {!checklist.isTemplate && (
              <SaveAsTemplateButton
                id={checklist.id}
                type="checklist"
                defaultName={`${title} Template`}
              />
            )}
            <DropdownMenu
              ariaLabel={`List ${title}`}
              align="end"
              trigger={(p) => (
                <button
                  type="button"
                  {...p}
                  aria-label={`Actions for ${title}`}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                >
                  <MoreHorizontal size={16} />
                </button>
              )}
              items={[
                {
                  id: 'section',
                  label: 'Add section',
                  icon: <Plus size={14} />,
                  onSelect: newSection,
                },
                {
                  id: 'settings',
                  label: 'List settings',
                  icon: <Settings2 size={14} />,
                  onSelect: () => setSettingsOpen(true),
                },
                {
                  id: 'print',
                  label: 'Print list',
                  icon: <Printer size={14} />,
                  onSelect: () => setPrintOpen(true),
                },
                checklist.isTemplate
                  ? {
                      id: 'archive',
                      label: 'Archive template',
                      icon: <Archive size={14} />,
                      dividerBefore: true,
                      onSelect: archiveList,
                    }
                  : {
                      id: 'delete',
                      label: 'Delete list',
                      icon: <Trash2 size={14} />,
                      dividerBefore: true,
                      onSelect: removeList,
                    },
              ]}
            />
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3 pl-8">
          <ProgressBar pct={progress.pct} label={`${title} progress`} />
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {progress.done} of {progress.total} done
          </span>
        </div>
        {(checklist.dueTimeEnabled || checklist.includeInDigest === false) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-8">
            {checklist.dueTimeEnabled && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Clock size={11} aria-hidden="true" /> Due times on
              </span>
            )}
            {checklist.includeInDigest === false && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <MailX size={11} aria-hidden="true" /> Not in the daily email
              </span>
            )}
          </div>
        )}
      </header>
      {settingsOpen && (
        <TaskListSettingsModal
          checklist={checklist}
          saving={updateChecklist.isPending}
          onClose={() => setSettingsOpen(false)}
          onSave={(updates) =>
            updateChecklist.mutate(
              { id: checklist.id, updates },
              { onSuccess: () => setSettingsOpen(false) },
            )
          }
        />
      )}
      {printOpen && (
        <TaskListPrintModal
          checklist={checklist}
          todayKey={props.todayKey}
          onClose={() => setPrintOpen(false)}
        />
      )}

      {expanded && (
        <div className="pb-1">
          {checklist.sections.map((section, i) => (
            <SectionBlock
              key={section.id}
              section={section}
              checklist={checklist}
              index={i}
              sectionCount={checklist.sections.length}
              props={props}
              onMove={move}
            />
          ))}
          {checklist.sections.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              This list has no sections.{' '}
              <button
                type="button"
                onClick={newSection}
                className="text-primary-700 hover:underline"
              >
                Add one
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function shownByStatus(task: ChecklistItem, status: TaskFilters['status']): boolean {
  if (status === 'all') return true;
  return isChecklistItemDone(task) === (status === 'done');
}

const isEmptyList = (c: Checklist) => c.sections.every((s) => s.items.length === 0);

function filteredLists(
  props: TaskListsViewProps,
  todayKey: string,
  currentUserId: string | undefined,
  held: ReadonlySet<string>,
): Checklist[] {
  const { filters } = props;
  const found: Checklist[] = [];
  const empty: Checklist[] = [];
  for (const c of props.checklists) {
    const hit =
      c.id === props.openId ||
      c.sections.some((s) =>
        s.items.some(
          (t) => matches(t, filters, todayKey, currentUserId) && shownByStatus(t, filters.status),
        ),
      );
    if (hit) found.push(c);
    else if (isEmptyList(c) || held.has(c.id)) empty.push(c);
  }
  return [...found, ...empty];
}

/** Every task list, each open, its sections one under another, tasks in the order they were put in. */
export function TaskListsView(props: TaskListsViewProps) {
  const { filters, todayKey, currentUserId } = props;
  const filtering =
    !!filters.q.trim() ||
    filters.assignee.length > 0 ||
    filters.due.length > 0 ||
    filters.status !== 'open';
  // With a filter on, page over the lists that have a match on show — not ten
  // cards ahead of the one list that holds it. With "open", finished tasks are
  // folded away, so a list whose only matches are finished does not count.
  // A list with no tasks yet stays too, so its first task can be added
  // without clearing the filter — but after every list with a match, so
  // empty lists never push what the filter found behind "Show more". The
  // list asked for by `openId` counts as a match.
  // A list shown because it was empty is held until the filter changes, so
  // adding its first task — which the filter may not match — never makes the
  // list vanish from under the producer.
  const filterKey = JSON.stringify(filters);
  const [held, setHeld] = useState<{ key: string; ids: Set<string> }>({
    key: filterKey,
    ids: new Set(props.checklists.filter(isEmptyList).map((c) => c.id)),
  });
  const emptyIds = props.checklists
    .filter(isEmptyList)
    .map((c) => c.id)
    .join(',');
  useEffect(() => {
    setHeld((prev) => {
      const ids = new Set(prev.key === filterKey ? prev.ids : []);
      for (const id of emptyIds.split(',').filter(Boolean)) ids.add(id);
      return { key: filterKey, ids };
    });
  }, [filterKey, emptyIds]);
  const heldIds = held.key === filterKey ? held.ids : new Set<string>();
  const candidates = filtering
    ? filteredLists(props, todayKey, currentUserId, heldIds)
    : props.checklists;
  // A list asked for by `openId` is always within the page shown.
  const openIndex = props.openId ? candidates.findIndex((c) => c.id === props.openId) : -1;
  const lists = usePaged(candidates, LIST_PAGE_SIZE, openIndex + 1);
  // One count request for the task rows on screen (a template's rows show no
  // conversation, so they are never counted).
  return (
    <TaskConversationsProvider>
      <div className="space-y-4">
        {lists.visible.map((checklist) => (
          <ChecklistCard key={checklist.id} checklist={checklist} props={props} />
        ))}
        <ShowMore hidden={lists.hidden} noun="lists" onClick={lists.showMore} />
      </div>
    </TaskConversationsProvider>
  );
}
